/**
 * Group IDs (§5). A player now types this in, on the device where the app already lives, so
 * the format is built for reading off a text message and typing on a phone: a three-letter
 * group prefix, then eight characters from an alphabet with no 0/O, no 1/I/L and no U/V to
 * second-guess. Displayed in threes and fours — `TUE-4K7M-9QX2`.
 *
 * The canonical form is the compact uppercase string (`TUE4K7M9QX2`); that is what gets
 * hashed, stored and sent as `X-Topspin-Key`, so hyphens, spaces and case in whatever the
 * player typed never matter.
 */

/** 30 characters, 8 of them: ~39 bits. Unguessable enough for a six-player tennis app (§8). */
export const GROUP_ID_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';

export const GROUP_ID_PREFIX_LENGTH = 3;
export const GROUP_ID_CODE_LENGTH = 8;

const CANONICAL_RE = new RegExp(
  `^[A-Z]{${GROUP_ID_PREFIX_LENGTH}}[${GROUP_ID_ALPHABET}]{${GROUP_ID_CODE_LENGTH}}$`,
);

/**
 * Canonical form of whatever the player typed, or `undefined` if it can't be one. Lenient
 * about case, spaces and punctuation on the way in — a group ID gets read aloud and retyped,
 * and "did that have dashes?" is not a question worth failing on.
 */
export function normalizeGroupId(input: string): string | undefined {
  const canonical = input.toUpperCase().replace(/[^A-Z0-9]/g, '');
  return CANONICAL_RE.test(canonical) ? canonical : undefined;
}

/** `TUE4K7M9QX2` → `TUE-4K7M-9QX2`, the only form a human should ever see. */
export function formatGroupId(canonical: string): string {
  const normalized = normalizeGroupId(canonical);
  if (!normalized) return canonical;
  const prefix = normalized.slice(0, GROUP_ID_PREFIX_LENGTH);
  const code = normalized.slice(GROUP_ID_PREFIX_LENGTH);
  return `${prefix}-${code.slice(0, 4)}-${code.slice(4)}`;
}

/**
 * Accepts a bare group ID or a whole `…/?k=<id>` link, because people paste both — the old
 * links still exist in everyone's text history, and the dev server prints them.
 */
export function parseGroupIdInput(input: string): string | undefined {
  const value = input.trim();
  if (!value) return undefined;
  if (/^https?:\/\//i.test(value)) {
    try {
      // Case-insensitive on the parameter name: the field upper-cases as you type, and a
      // pasted link goes through it too.
      for (const [name, param] of new URL(value).searchParams) {
        if (name.toLowerCase() === 'k') return normalizeGroupId(param);
      }
      return undefined;
    } catch {
      return undefined;
    }
  }
  return normalizeGroupId(value);
}

/**
 * Three letters that tell two IDs apart in a text message. Derived from the groups the ID
 * covers, so the Tuesday group's ID reads `TUE-…`; an ID spanning both is `ALL-…`.
 */
export function groupIdPrefix(groupIds: readonly string[]): string {
  if (groupIds.length !== 1) return 'ALL';
  const letters = (groupIds[0] ?? '').toUpperCase().replace(/[^A-Z]/g, '');
  return letters.length >= GROUP_ID_PREFIX_LENGTH
    ? letters.slice(0, GROUP_ID_PREFIX_LENGTH)
    : 'GRP';
}

/** True for a prefix `mint-token` may use — three letters, so every ID is the same shape. */
export function isValidGroupIdPrefix(prefix: string): boolean {
  return new RegExp(`^[A-Z]{${GROUP_ID_PREFIX_LENGTH}}$`).test(prefix);
}

/**
 * A fresh group ID. Rejection sampling rather than `% 30`, which would make the first two
 * letters of the alphabet a little likelier than the rest.
 */
export function generateGroupId(prefix: string): string {
  if (!isValidGroupIdPrefix(prefix)) {
    throw new Error(`group ID prefix must be ${GROUP_ID_PREFIX_LENGTH} uppercase letters`);
  }
  const size = GROUP_ID_ALPHABET.length;
  const limit = Math.floor(256 / size) * size;
  let code = '';
  const bytes = new Uint8Array(GROUP_ID_CODE_LENGTH * 2);
  while (code.length < GROUP_ID_CODE_LENGTH) {
    crypto.getRandomValues(bytes);
    for (const byte of bytes) {
      if (byte >= limit) continue;
      code += GROUP_ID_ALPHABET[byte % size];
      if (code.length === GROUP_ID_CODE_LENGTH) break;
    }
  }
  return `${prefix}${code}`;
}
